import { useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn, formatBytes } from '@/lib/utils';
import type { StorageGrowthPoint } from '@/hooks/useStorageGrowth';

/**
 * Bytes added per time bucket. Bucket labels are pre-formatted by
 * useStorageGrowth for the active timeframe, so the axis renders them as-is.
 *
 * Includes a self-contained Linear / Log scale toggle. Log mode uses
 * domain={[1, 'dataMax']} so zero-byte buckets clamp to the 1-byte floor
 * instead of producing NaN or a blank series.
 */
export function StorageGrowthChart({ data }: { data: StorageGrowthPoint[] }) {
  const [logScale, setLogScale] = useState(false);

  if (!data.length) {
    return (
      <div className="flex h-48 items-center justify-center text-xs text-muted-foreground">
        No storage growth in window.
      </div>
    );
  }

  // Log scale cannot plot 0. Replace zeros with 1 (1 byte) so the area
  // renders at the floor rather than going blank or producing NaN ticks.
  const chartData = logScale
    ? data.map((pt) => ({ ...pt, bytes: pt.bytes > 0 ? pt.bytes : 1 }))
    : data;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-end gap-1">
        <span className="text-xs text-muted-foreground">Scale</span>
        <div className="inline-flex rounded-md border border-border text-xs">
          <button
            onClick={() => setLogScale(false)}
            className={cn(
              'rounded-l-md px-2.5 py-1 font-medium transition-colors',
              !logScale
                ? 'bg-muted text-foreground'
                : 'bg-transparent text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={!logScale}
          >
            Linear
          </button>
          <button
            onClick={() => setLogScale(true)}
            className={cn(
              'rounded-r-md border-l border-border px-2.5 py-1 font-medium transition-colors',
              logScale
                ? 'bg-muted text-foreground'
                : 'bg-transparent text-muted-foreground hover:text-foreground',
            )}
            aria-pressed={logScale}
          >
            Log
          </button>
        </div>
      </div>

      <div className="h-48 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="storageFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(160 84% 45%)" stopOpacity={0.45} />
                <stop offset="100%" stopColor="hsl(160 84% 45%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 18% 18%)" />
            <XAxis dataKey="bucket" tick={{ fill: 'hsl(215 14% 65%)', fontSize: 11 }} stroke="hsl(222 18% 18%)" />
            <YAxis
              tick={{ fill: 'hsl(215 14% 65%)', fontSize: 11 }}
              stroke="hsl(222 18% 18%)"
              tickFormatter={(v: number) => formatBytes(v)}
              width={64}
              {...(logScale
                ? { scale: 'log', domain: [1, 'dataMax'], allowDataOverflow: true }
                : {})}
            />
            <Tooltip
              contentStyle={{
                background: 'hsl(222 24% 9%)',
                border: '1px solid hsl(222 18% 18%)',
                borderRadius: 8,
                fontSize: 12,
              }}
              formatter={(value: number) => [
                // In log mode the tooltip value comes from the clamped chartData
                // (zeros become 1). Show "0 B" for those so the user sees the
                // original meaning, not the floor sentinel.
                formatBytes(logScale && value === 1 ? 0 : value),
                'added',
              ]}
            />
            <Area type="monotone" dataKey="bytes" stroke="hsl(160 84% 45%)" fill="url(#storageFill)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
